import mapboxgl from 'mapbox-gl'

const { cluster_properties, cluster_conditions, map_icons, map_icon_size } = window.mapbox_data.map_data

const getPopupHtml = (location, assets_count) => {
  const category = location.category

  if (window.locationPopupTemplates && window.locationPopupTemplates[category]) {
    const customHtml = window.locationPopupTemplates[category](location, assets_count)
    if (customHtml != null) {
      return customHtml
    }
  }

  return defaultPopupTemplate(location)
}

const defaultPopupTemplate = (location) => {
  const { name, category, state, state_tag, kg_asset_name, tvid } = location


  return `
    <div class="flex flex-col">

    <div class="flex justify-between items-center">
      <h6>${name}</h6>
      ${tvid ? `<span>TVID: ${tvid}</span>` : ''}
    </div>
    <div class="flex justify-between items-center gap-2">
      <div class="mt-1 p2 text-xs">${category === 'legacy' ? 'Legacy Location' : 'Shelter Location'}</div>
      ${category === 'legacy' ? (kg_asset_name ? `<span>AssetName: ${kg_asset_name}</span>` : '') : (state ? `<span class="tag ${state_tag}-tag">${state}</span>` : '')}
    </div>
  </div>`
}


const initLocationMap = (id, coordinates, zoom, hash) => {
  mapboxgl.accessToken = window.mapbox_data.access_token
  const map = new mapboxgl.Map({
    container: id,
    style: 'mapbox://styles/mapbox/streets-v9',
    center: coordinates,
    zoom: zoom,
    hash: hash
  })
  map.addControl(new mapboxgl.NavigationControl(), 'bottom-right')
  return map
}

const renderMapLocations = (map, locations, show_popup = true) => {
  const mapData = createFeatureCollection(locations)

  map.loaded()
    ? loadMapData(map, mapData, show_popup)
    : map.on('load', () => loadMapData(map, mapData, show_popup))
}

const loadMapData = function (map, mapData, show_popup) {
  loadMapImages(map, map_icons).then(() => {
    map.addSource('mapData', {
      type: 'geojson',
      data: mapData,
      cluster: true,
      clusterMaxZoom: 18,
      clusterRadius: 50,
      clusterProperties: cluster_properties
    })

    map.addLayer({
      id: 'clusters',
      type: 'symbol',
      source: 'mapData',
      filter: ['has', 'point_count'],
      layout: {
        'icon-image': cluster_conditions,
        'icon-size': map_icon_size,
        'icon-allow-overlap': true,
      },
    })

    map.addLayer({
      id: 'clusters-count-bg',
      type: 'circle',
      source: 'mapData',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#51bbd6',
        'circle-radius': 8,
        'circle-translate': [-15, -15],
      },
    })

    map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'mapData',
      filter: ['has', 'point_count'],
      paint: {
        'text-translate': [-15, -15],
      },
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-allow-overlap': true,
      },
    })

    map.addLayer({
      id: 'unclustered-point',
      type: 'symbol',
      source: 'mapData',
      layout: {
        'icon-image': ['get', 'icon_type'],
        'icon-size': map_icon_size,
        'icon-allow-overlap': true,
      },
    })

    map.on('click', 'clusters', (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] })
      const clusterId = features[0].properties.cluster_id
      map.getSource('mapData').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return

        map.easeTo({ center: features[0].geometry.coordinates, zoom: zoom })
      })
    })

    if (show_popup === true) {
      map.on('click', 'unclustered-point', (e) => {
        const coordinates = e.features[0].geometry.coordinates.slice()
        const location = e.features[0].properties
        const assets_count = JSON.parse(location.assets)?.length

        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360

        const mapPinHtml = getPopupHtml(location, assets_count)
        new mapboxgl.Popup().setLngLat(coordinates).setHTML(mapPinHtml).addTo(map)
      })
    }

    map.on('mouseenter', 'clusters', () => map.getCanvas().style.cursor = 'pointer' )
    map.on('mouseleave', 'clusters', () => map.getCanvas().style.cursor = '' )
  })
}

const loadMapImages = (map, map_icons) => {
  const addImage = (map, id, url) => {
    return new Promise((resolve, reject) => {
      map.loadImage(url, (error, image) => {
        if (error) {
          reject(error)
          return
        }
        map.addImage(id, image)
        resolve(image)
      })
    })
  }

  const update_icons = [...new Map(map_icons.map(icon => [icon.id, icon])).values()]
  const promises = update_icons.map((imageData) => addImage(map, imageData.id, imageData.url))
  return Promise.all(promises)
}

const updateMapLocations = (map, locations) => {
  const mapData = createFeatureCollection(locations)
  map.getSource('mapData').setData(mapData)
}

const createFeatureCollection = (locations) => {
  return {
    type: 'FeatureCollection',
    features: locations.map((location) => createFeature(location))
  }
}

const createFeature = (location) => {
  return {
    type: 'Feature',
    properties: location,
    geometry: {
      type: 'point',
      coordinates: [location.longitude, location.latitude]
    }
  }
}

const recenterMapToLocations = (map, locations) => {
  if (!map || !locations || locations.length === 0) return

  const valid = locations.filter(loc => loc.longitude && loc.latitude)
  if (valid.length === 0) return

  if (valid.length === 1) {
    const loc = valid[0]
    map.easeTo({
      center: [loc.longitude, loc.latitude],
      zoom: 14
    })
  } else {
    const bounds = new mapboxgl.LngLatBounds()
    valid.forEach(loc => bounds.extend([loc.longitude, loc.latitude]))
    map.fitBounds(bounds, { padding: 50 })
  }
}



const addMarker = (map, coordinates, category, draggable) => {
  const icon = document.createElement('div')
  icon.innerHTML = `<img src="${map_icons.find(obj => obj.id === category)?.url}" width="32" height="32" />`

  new mapboxgl.Marker({
    element: icon,
    draggable: draggable,
  }).setLngLat(coordinates).addTo(map)
}

export { addMarker, initLocationMap, renderMapLocations, updateMapLocations, recenterMapToLocations }
